import React, { lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

import AppUnavailable from '../components/app-unavailable';
import ErrorBoundary from '../components/error-boundary';

import authRoutes from './auth-routes';
import duelRoutes from './duel-routes';
import profileRoutes from './profile-routes';
import quizRoutes from './quiz-routes';
import subscriptionRoutes from './subscription-routes';
import dailyTipperRoutes from './daily-tipper-routes';
import classroomRoutes from './classroom-routes';
import onboardingRoutes from './onboarding-routes';

import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { useRouteLogic } from '../hooks/useRouteLogic';
import PrivateRoutesWrapper from './private-routes-wrapper';
import { DailyTipperDataProvider } from '@/contexts/daily-tipper-data-context';
import { useAuth } from '@/contexts/auth-context';
import RnWebView from '../components/rn-web-view';
import { useGa4RouteTracking } from '@/analytics/useGa4RouteTracking';
import { useSessionReplayScope } from '@/analytics/useSessionReplayScope';

// Lazy loaded components
const GoTo = lazy(() => import('../screens/deep-links/go-to'));
const PublicQuizTimeDetails = lazy(() => import('../screens/main/quiztime/public-quiztime-details'));
const PublicClassRoomDetails = lazy(
  () => import('../screens/main/classrooms/pages/public-classroom-details')
);
const StartDailyTipper = lazy(() => import('../screens/main/start-daily-tipper'));
const Home = lazy(() => import('../screens/main/home'));
const InviteFriends = lazy(() => import('../screens/main/invite-friends'));
const MyLearningProgress = lazy(() => import('../screens/main/my-learning-progress'));
const DailyAnsweringStats = lazy(
  () => import('../screens/main/my-learning-progress/pages/daily-answering-stats')
);

const AppRoutes: React.FC = () => {
  useGa4RouteTracking();
  useSessionReplayScope();

  const location = useLocation();
  const { shouldRedirectToConfirmEmail, shouldShowDailyTipper } = useRouteLogic();
  const { isAuthorized } = useAuth();

  const { humeoAppEnabled, inviteFriendsEnabled, classroomsEnabled, dailyTipperEnabled } =
    useFeatureFlags();

  if (humeoAppEnabled === false) {
    return <AppUnavailable />;
  }

  if (shouldRedirectToConfirmEmail) {
    return <Navigate to="/sign-up-confirm-email" />;
  }

  return (
    <>
      {/* Per-route boundary: a screen crash falls back here and auto-recovers when the user navigates,
          instead of taking the whole app down. The app-level boundary in app.tsx is the outer net. */}
      <ErrorBoundary resetKeys={[location.pathname]}>
        <Routes>
          {/* Public routes */}
          {authRoutes}
          <Route path="/goto/:hash/" element={<GoTo />} />
          <Route
            path="/public-classroom-details/:sessionId/:hash"
            element={<PublicClassRoomDetails />}
          />
          <Route
            path="/public-quiztime-details/:quizSessionId/:hash"
            element={<PublicQuizTimeDetails />}
          />
          {/* Private routes */}
          <Route element={<PrivateRoutesWrapper />}>
            <Route element={<DailyTipperDataProvider />}>
              <Route
                path="/home"
                element={
                  <>
                    <Home />
                    {shouldShowDailyTipper && <StartDailyTipper />}
                  </>
                }
              />
              {dailyTipperEnabled && dailyTipperRoutes}
            </Route>
            {classroomsEnabled && classroomRoutes}
            {duelRoutes}
            {quizRoutes}
            {profileRoutes}
            {subscriptionRoutes}
            {onboardingRoutes}
            <Route path="/my-learning-progress" element={<MyLearningProgress />} />
            <Route
              path="/my-learning-progress/daily-answering-stats"
              element={<DailyAnsweringStats />}
            />
            <Route
              path="/invite-friends"
              element={inviteFriendsEnabled ? <InviteFriends /> : <Navigate to="/home" />}
            />
          </Route>
          {/* Redirects and Fallbacks */}
          <Route path="/" element={<Navigate to={isAuthorized ? '/home' : '/auth'} replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ErrorBoundary>
      {!!window?.ReactNativeWebView && <RnWebView />}
    </>
  );
};

export default AppRoutes;
